import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  ModalLayout,
  ModalLayoutRef,
} from "../ui/common_components/ModalLayout";

interface ModalContent {
  children: ReactNode;
  className?: string;
}

interface ProjectModalContextType {
  show: (content: ModalContent) => void;
  close: () => void;
  isOpen: boolean;
}

const ProjectModalContext = createContext<ProjectModalContextType | undefined>(
  undefined
);

export function useProjectModal() {
  const ctx = useContext(ProjectModalContext);
  if (!ctx)
    throw new Error(
      "useProjectModal must be used within a ProjectModalProvider"
    );
  return ctx;
}

export function ProjectModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent | null>(null);
  const modalRef = useRef<ModalLayoutRef | null>(null);

  const show = useCallback((content: ModalContent) => {
    setModalContent(content);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (modalRef.current) {
      modalRef.current.triggerClose();
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setModalContent(null);
  }, []);

  return (
    <ProjectModalContext.Provider value={{ show, close, isOpen }}>
      {children}
      {isOpen && modalContent && (
        <ModalLayout
          ref={modalRef}
          onClose={handleClose}
          className={modalContent.className}
        >
          {modalContent.children}
        </ModalLayout>
      )}
    </ProjectModalContext.Provider>
  );
}
